import { RefObject, useEffect, useRef, useState } from "react";

/**
 * Bridges a dropzone to the Asgard Outlook Drop Helper, so users can drag a
 * message straight out of the new Outlook client onto the page.
 *
 * A browser cannot read an email out of such a drag on its own: Outlook hands
 * the message over through OS mechanisms (an async file drop on Windows, a file
 * promise on macOS) that Chromium's web drop path does not implement. The
 * helper is a local app that acts as the drop target instead and passes the
 * .eml back here.
 *
 * Does nothing when the helper is not installed, leaving ordinary HTML5
 * drag-and-drop untouched.
 */

const HELPER_ORIGIN = "http://127.0.0.1:8765";
const PROBE_MIN_DELAY_MS = 5000;
const PROBE_MAX_DELAY_MS = 60000;
const ZONE_THROTTLE_MS = 150;

interface CaughtFileMeta {
  id: string;
  name: string;
  size: number;
}

interface UseOutlookDropHelperArgs {
  /** The element whose screen position the helper should watch. */
  zoneRef: RefObject<HTMLElement | null>;
  /** Called with the emails the helper caught, same shape as a real file drop. */
  onFiles: (files: File[]) => void;
  enabled: boolean;
}

interface ZonePayload {
  x: number;
  y: number;
  width: number;
  height: number;
  scale: number;
  visible: boolean;
}

function describeZone(element: HTMLElement): ZonePayload {
  const rect = element.getBoundingClientRect();
  return {
    x: window.screenX + rect.left,
    // Browser chrome sits above the viewport and is not part of screenY.
    y: window.screenY + (window.outerHeight - window.innerHeight) + rect.top,
    width: rect.width,
    height: rect.height,
    // Windows places its catcher in physical pixels; macOS ignores this.
    scale: window.devicePixelRatio,
    visible: document.visibilityState === "visible",
  };
}

export function useOutlookDropHelper({
  zoneRef,
  onFiles,
  enabled,
}: UseOutlookDropHelperArgs): {
  helperConnected: boolean;
  /**
   * True while an Outlook drag is over the dropzone. The helper's catcher
   * window sits above the page and swallows the native drag events, so this is
   * the page's only way to render its usual drag-over highlight.
   */
  helperDragging: boolean;
} {
  const [helperConnected, setHelperConnected] = useState(false);
  const [helperDragging, setHelperDragging] = useState(false);

  // Kept in a ref so a new callback identity does not tear down the poll loop.
  const onFilesRef = useRef(onFiles);
  useEffect(() => {
    onFilesRef.current = onFiles;
  }, [onFiles]);

  // Connect to the helper and receive whatever it catches.
  useEffect(() => {
    if (!enabled) {
      setHelperConnected(false);
      setHelperDragging(false);
      return;
    }

    let cancelled = false;
    const controller = new AbortController();
    let probeDelay = PROBE_MIN_DELAY_MS;
    let wake: (() => void) | null = null;

    const sleep = (ms: number): Promise<void> =>
      new Promise((resolve) => {
        const timer = window.setTimeout(resolve, ms);
        wake = () => {
          window.clearTimeout(timer);
          resolve();
        };
      });

    // Coming back to the tab is the likeliest moment for the helper to have
    // been started, so retry immediately instead of waiting out the backoff.
    const onVisibilityChange = (): void => {
      if (document.visibilityState === "visible") {
        probeDelay = PROBE_MIN_DELAY_MS;
        wake?.();
      }
    };
    document.addEventListener("visibilitychange", onVisibilityChange);

    const collectFiles = async (metas: CaughtFileMeta[]): Promise<File[]> => {
      const files: File[] = [];
      for (const meta of metas) {
        const response = await fetch(`${HELPER_ORIGIN}/file/${meta.id}`, {
          signal: controller.signal,
        });
        if (!response.ok) continue;
        const blob = await response.blob();
        files.push(new File([blob], meta.name, { type: "message/rfc822" }));
      }
      return files;
    };

    const run = async (): Promise<void> => {
      let connected = false;
      while (!cancelled) {
        try {
          if (!connected) {
            const health = await fetch(`${HELPER_ORIGIN}/health`, {
              signal: controller.signal,
            });
            if (!health.ok) throw new Error("helper unhealthy");
            connected = true;
            probeDelay = PROBE_MIN_DELAY_MS;
            if (cancelled) return;
            setHelperConnected(true);
            continue;
          }

          // Long-poll; the helper parks this until it catches something.
          const response = await fetch(`${HELPER_ORIGIN}/wait`, {
            signal: controller.signal,
          });
          if (!response.ok) throw new Error("helper wait failed");
          const payload: { files?: CaughtFileMeta[]; dragging?: boolean } =
            await response.json();
          if (cancelled) return;
          // The mac helper omits the field; it never needs to relay drag state
          // because macOS still delivers dragenter to the page under the
          // catcher.
          setHelperDragging(payload.dragging ?? false);
          const files = await collectFiles(payload.files ?? []);
          if (cancelled) return;
          if (files.length > 0) onFilesRef.current(files);
        } catch {
          if (cancelled) return;
          const wasConnected = connected;
          connected = false;
          setHelperConnected(false);
          setHelperDragging(false);
          await sleep(probeDelay);
          // Most users will never install the helper. Each failed probe is an
          // uncatchable console error and a Sentry breadcrumb, so back off
          // rather than burying real breadcrumbs under localhost noise.
          if (!wasConnected) {
            probeDelay = Math.min(probeDelay * 2, PROBE_MAX_DELAY_MS);
          }
        }
      }
    };

    void run();

    return () => {
      cancelled = true;
      controller.abort();
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, [enabled]);

  // Tell the helper where the dropzone is. Only runs while it is connected, so
  // a machine without the helper makes no traffic beyond the backed-off probe.
  useEffect(() => {
    if (!enabled || !helperConnected) return;

    let lastSent = "";
    let pending = 0;

    const send = (): void => {
      const element = zoneRef.current;
      if (!element) return;
      const payload = describeZone(element);
      const key = JSON.stringify(payload);
      // Scroll and resize fire far more often than the zone actually moves.
      if (key === lastSent) return;
      lastSent = key;
      void fetch(`${HELPER_ORIGIN}/zone`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      }).catch(() => {
        // The poll loop owns connection state; nothing to do here.
      });
    };

    const schedule = (): void => {
      if (pending) return;
      pending = window.setTimeout(() => {
        pending = 0;
        send();
      }, ZONE_THROTTLE_MS);
    };

    send();
    const interval = window.setInterval(schedule, 1000);
    window.addEventListener("resize", schedule);
    // Capture phase, so scrolling any container that moves the zone counts.
    window.addEventListener("scroll", schedule, true);
    document.addEventListener("visibilitychange", schedule);

    return () => {
      if (pending) window.clearTimeout(pending);
      window.clearInterval(interval);
      window.removeEventListener("resize", schedule);
      window.removeEventListener("scroll", schedule, true);
      document.removeEventListener("visibilitychange", schedule);
      // Disarm the helper so it stops covering a dropzone that is gone.
      void fetch(`${HELPER_ORIGIN}/zone`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          x: 0,
          y: 0,
          width: 0,
          height: 0,
          scale: 1,
          visible: false,
        }),
        keepalive: true,
      }).catch(() => {
        // Nothing to do when the helper is already gone.
      });
    };
  }, [enabled, helperConnected, zoneRef]);

  return { helperConnected, helperDragging };
}
